import { useEffect, useRef } from 'react';
import { createCamera2D } from '../../engine/hex/camera2d';
import { createMapModel } from '../../engine/map/mapModel';
import { generateIslandTiles } from '../../engine/map/islandGenerator';
import { loadGameConfig, mergeGameConfig, type GameConfig } from '../../config/gameConfig';
import { axialToPixel, hexVertices } from '../../engine/hex/hexMath';
import { renderHex } from '../../engine/render/hex/renderHex';
import type { HexMapState } from '../../engine/render/renderer';
import { IconRegistry } from '../overlay/assets/IconRegistry';
import { SoundManager } from '../../audio/SoundManager';

const MENU_SEED_RANGE = 100000;
const MENU_SWAP_MS = 3000;
const MENU_TARGET_MS = 3000;

function createMenuState(config: GameConfig): HexMapState {
    return {
        pointer: { x: 0, y: 0 },
        dragging: false,
        dragStart: { x: 0, y: 0 },
        camStart: { x: 0, y: 0 },
        hover: null,
        ownedTileIds: new Set(),
        discoveredTileIds: new Set(),
        settlementsById: {},
        settlementByTileId: {},
        foundingExpeditions: [],
        expeditionIdCounter: 0,
        transportRoutes: [],
        transportIdCounter: 0,
        scoutingMissions: [],
        scoutingIdCounter: 0,
        battleReports: [],
        battleReportIdCounter: 0,
        scoutPlanningMissionId: null,
        enemyForces: {},
        dismissedNotifications: new Set(),
        alerts: [],
        alertIdCounter: 0,
        overlay: {
            showNotifications: false,
            speedMenuOpen: false,
            languageMenuOpen: false,
        },
        config,
        settingsEditor: { text: '', error: null },
        modals: [],
        activeModalId: null,
        modalDragId: null,
        modalDragOffset: { x: 0, y: 0 },
        isModalHover: false,
        actionBarActions: [],
        overlayAssets: { icons: new IconRegistry() },
        audio: { manager: new SoundManager(() => ({}) as HTMLAudioElement) },
        tutorial: null,
        simulation: { running: false, speed: 1, accumulator: 0, lastTime: 0, tick: 0 },
        dirty: false,
    };
}

function randomSeed() {
    return Math.floor(Math.random() * MENU_SEED_RANGE);
}

function pickRange(size: GameConfig['map']['size']): number {
    if (size === 'small') return 8;
    if (size === 'medium') return 12;
    return 16;
}

export function useMenuBackground(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
    const stateRef = useRef<HexMapState | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const viewport = { width: window.innerWidth, height: window.innerHeight };
        const camera = createCamera2D(() => viewport);
        const baseConfig = loadGameConfig();
        let config = mergeGameConfig(baseConfig, {
            map: { size: baseConfig.map.size, seed: randomSeed() },
        });
        let model = createMapModel(generateIslandTiles(config));
        let state = createMenuState(config);
        state.discoveredTileIds = new Set(model.tiles.map((tile) => tile.id));
        stateRef.current = state;

        let lastSwap = performance.now();
        let lastTarget = performance.now();
        let target = { offsetX: 0, offsetY: 0, zoom: 1 };

        const pickTarget = () => {
            const range = pickRange(config.map.size);
            target = {
                offsetX: (Math.random() - 0.5) * range,
                offsetY: (Math.random() - 0.5) * range,
                zoom: 0.6 + Math.random() * 0.8,
            };
        };

        pickTarget();

        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            viewport.width = window.innerWidth;
            viewport.height = window.innerHeight;
            canvas.width = Math.floor(viewport.width * dpr);
            canvas.height = Math.floor(viewport.height * dpr);
            canvas.style.width = `${viewport.width}px`;
            canvas.style.height = `${viewport.height}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        const draw = () => {
            ctx.clearRect(0, 0, viewport.width, viewport.height);
            ctx.save();
            ctx.translate(viewport.width / 2, viewport.height / 2);
            ctx.scale(camera.zoom, camera.zoom);
            ctx.translate(camera.offsetX, camera.offsetY);
            ctx.globalAlpha = 0.6;
            for (const tile of model.tiles) {
                const axial = { q: tile.position.q, r: tile.position.r };
                const center = axialToPixel(axial);
                const verts = hexVertices(center);
                renderHex({
                    ctx,
                    tile,
                    center,
                    verts,
                    zoom: camera.zoom,
                    state,
                    settlement: null,
                });
            }
            ctx.restore();
            ctx.fillStyle = 'rgba(8,8,10,0.55)';
            ctx.fillRect(0, 0, viewport.width, viewport.height);
        };

        let frame = 0;
        const tick = (time: number) => {
            if (time - lastSwap > MENU_SWAP_MS) {
                config = mergeGameConfig(baseConfig, {
                    map: { ...config.map, seed: randomSeed() },
                });
                model = createMapModel(generateIslandTiles(config));
                state = createMenuState(config);
                state.discoveredTileIds = new Set(model.tiles.map((tile) => tile.id));
                stateRef.current = state;
                lastSwap = time;
                pickTarget();
                lastTarget = time;
            }
            if (time - lastTarget > MENU_TARGET_MS) {
                pickTarget();
                lastTarget = time;
            }
            camera.offsetX += (target.offsetX - camera.offsetX) * 0.01;
            camera.offsetY += (target.offsetY - camera.offsetY) * 0.01;
            camera.zoom += (target.zoom - camera.zoom) * 0.01;
            draw();
            frame = window.requestAnimationFrame(tick);
        };

        resize();
        window.addEventListener('resize', resize);
        frame = window.requestAnimationFrame(tick);

        return () => {
            window.removeEventListener('resize', resize);
            window.cancelAnimationFrame(frame);
        };
    }, [canvasRef]);
}
